import {
  PaginatedAPIResponse,
  SortDirection,
  WorkOrderDashboardPageTabs,
  WorkOrderStatus,
  WorkOrderSummaryData,
  WorkOrderType,
  WorkOrdersCriteria,
} from '@zspace/types';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigation,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { fetchWorkOrders } from '../work-orders-service';

import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import { WorkOrderDashboardPageContext } from '../work-orders-dashboard-page/types';
import WorkOrdersTableCard from '../work-orders-table-card/work-orders-table-card';

import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import Spinner from '../../ui/spinner/spinner';

const initialCriteriaData = (searchParams?: URLSearchParams) => ({
  itemsPerPage: parseInt(
    getSearchParamValue(searchParams, 'itemsPerPage') ?? '10'
  ),
  pageNumber: parseInt(getSearchParamValue(searchParams, 'pageNumber') ?? '1'),
  search: getSearchParamValue(searchParams, 'search') ?? '',
  sortBy: getSearchParamValue(searchParams, 'sortBy') ?? 'createdAt',
  sortDirection:
    getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
    SortDirection.DES,
  workOrderName: getSearchParamValues(searchParams, 'workOrderName'),
  accounts: getSearchParamValues(searchParams, 'accounts'),
  type: getSearchParamValues<WorkOrderType>(searchParams, 'type'),
  createdBy: getSearchParamValues(searchParams, 'createdBy'),
  salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
  status: WorkOrderStatus.COMPLETED,
});

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialCriteriaData(searchParams);

  const response = fetchWorkOrders(criteria);

  return defer({ response });
};

function WorkOrdersCompletedTabContent() {
  const { setActiveTab, setShowCreateWorkOrderButton } =
    useOutletContext<WorkOrderDashboardPageContext>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const response =
    useAsyncValue() as PaginatedAPIResponse<WorkOrderSummaryData>;

  const [data, setData] = useState<WorkOrdersCriteria>(
    initialCriteriaData(searchParams)
  );

  const emptyContent = useCallback(
    (emptyText: string) => (
      <Element display="flex" justifyContent="center">
        <span className="my-2">{emptyText}</span>
      </Element>
    ),
    []
  );

  const emptyTableContent = emptyContent(
    'There are no completed work orders at the moment'
  );

  const emptyTableContentWithFilter = emptyContent(
    'There are no completed work orders that match the selected criteria'
  );

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  useEffect(() => {
    setActiveTab(WorkOrderDashboardPageTabs.COMPLETED);
  }, [setActiveTab]);

  useEffect(() => {
    setShowCreateWorkOrderButton(true);
  }, [setShowCreateWorkOrderButton]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <Spinner />
      </Element>
    );
  }

  return (
    <WorkOrdersTableCard
      criteria={data}
      tableData={response}
      initialCriteria={initialCriteriaData()}
      setCriteria={setData}
      workOrdersStatus={WorkOrderStatus.COMPLETED}
      emptyTableContent={emptyTableContent}
      emptyTableContentWithFilters={emptyTableContentWithFilter}
    />
  );
}

export function WorkOrdersCompletedTab() {
  const { response } = useLoaderData() as {
    response: Promise<PaginatedAPIResponse<WorkOrderSummaryData>>;
  };

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <WorkOrdersCompletedTabContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default WorkOrdersCompletedTab;
